<template>
  <div class="dashboard-container">
    <!-- Top Navigation Bar -->
    <header class="dashboard-header">
      <div class="header-left">
        <div class="logo-section" @click="navigateToPage('DashboardModule')">
          <img src="/openpsychiclogo.png" alt="OpenPsychic" class="logo-img" />
          <span class="logo-text">OpenPsychic</span>
        </div>
      </div>

      <div class="header-right">
        <button class="profile-button" @click="navigateToPage('SettingsModule')">
          <img :src="userProfileImage || defaultProfileImage" alt="Profile" class="profile-avatar" />
        </button>
      </div>
    </header>

    <!-- Main Content Area -->
    <main class="dashboard-content">
      <component :is="currentComponent" />
    </main>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import DashboardModule from './DashboardModule.vue';
import MessagesModule from './MessagesModule.vue';
import SettingsModule from './SettingsModule.vue';
import CalendarModule from './CalendarModule.vue';

export default {
  name: 'AppDashboard',
  components: {
    DashboardModule,
    MessagesModule,
    SettingsModule,
    CalendarModule,
  },
  data() {
    return {
      userProfileImage: null,
      defaultProfileImage: require('@/assets/default-profile.png'),
      menuItems: [
        { label: 'Dashboard', component: 'DashboardModule', icon: require('@/assets/home-icon.png') },
        { label: 'Messages', component: 'MessagesModule', icon: require('@/assets/messages-icon.png') },
        { label: 'Calendar', component: 'CalendarModule', icon: require('@/assets/data.png') },
        { label: 'Settings', component: 'SettingsModule', icon: require('@/assets/001-setting-bulb.png') },
      ],
    };
  },
  computed: {
    ...mapState(['currentPage']),
    currentComponent() {
      return {
        DashboardModule,
        MessagesModule,
        SettingsModule,
        CalendarModule,
      }[this.currentPage] || DashboardModule;
    },
  },
  methods: {
    ...mapMutations(['setCurrentPage']),
    navigateToPage(page) {
      this.setCurrentPage(page);
    },
    async fetchUserProfile() {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          const db = getFirestore();
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            this.userProfileImage = userData.profileimgsrc || this.defaultProfileImage;
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    },
  },
  mounted() {
    this.fetchUserProfile();
    // Set default page if none is set
    if (!this.currentPage) {
      this.setCurrentPage('DashboardModule');
    }
  },
};
</script>

<style scoped>
.dashboard-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #16041E;
  overflow: hidden;
}

/* Header Styles */
.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, #2a093a 0%, #16041E 100%);
  border-bottom: 1px solid rgba(219, 143, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  position: relative;
}

.header-left {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.logo-section:hover {
  opacity: 0.8;
}

.logo-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.logo-text {
  font-size: 20px;
  font-weight: 700;
  color: #DB8FFF;
  letter-spacing: 0.5px;
  text-shadow: 0 0 10px rgba(219, 143, 255, 0.5);
}


.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}

.profile-button {
  background: transparent;
  border: 2px solid rgba(219, 143, 255, 0.3);
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 40px;
  height: 40px;
  overflow: hidden;
}

.profile-button:hover {
  border-color: #DB8FFF;
  transform: scale(1.05);
}

.profile-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Main Content */
.dashboard-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #16041E;
}

.dashboard-content::-webkit-scrollbar {
  width: 8px;
}

.dashboard-content::-webkit-scrollbar-track {
  background: #2a093a;
}

.dashboard-content::-webkit-scrollbar-thumb {
  background: #DB8FFF;
  border-radius: 4px;
}

.dashboard-content::-webkit-scrollbar-thumb:hover {
  background: #ECC3FF;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .dashboard-header {
    padding: 12px 16px;
    flex-wrap: wrap;
  }

  .logo-text {
    font-size: 16px;
  }

  .dashboard-content {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .logo-text {
    display: none;
  }
}
</style>